import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import type { BackendApi } from "../../api/api.ts";
import type { DocumentDirection } from "../../generated/enums.ts";
import { getCurrentBusiness } from "../../utils/OrgManagement.ts";
import { hasConfirmedLeaving, setHasPendingUploads } from "../../utils/pendingUploads.ts";

export const UPLOAD_CONCURRENCY = 2;
export const MAX_RETRIES = 3;
const DEFAULT_RETRY_BASE_DELAY_MS = 1000;
const MAX_PAUSE_MS = 30 * 1000;
export const MAX_CONSECUTIVE_FAILURES = 5;
const PUBLISH_INTERVAL_MS = 250;

export type UploadQueueStatus = 'queued' | 'uploading' | 'done' | 'failed';

export type UploadQueueError = 'rejected' | 'upload_failed';

export interface UploadQueueItem {
    id: string;
    files: File[];
    direction: DocumentDirection;
    uploadBatchId: string;
    businessId: string | null;
    status: UploadQueueStatus;
    error?: UploadQueueError;
}

export type UploadQueueApi = Pick<BackendApi, 'postFormData'>;

interface UseUploadQueueOptions {
    api: UploadQueueApi;
    onAllSettled?: () => void;
    retryBaseDelayMs?: number;
}

type UploadOutcome =
    | { kind: 'done' | 'rejected' | 'aborted' }
    | { kind: 'retry'; retryAfterMs: number | null };

function baseNameOf(file: File): string {
    const dot = file.name.lastIndexOf('.');
    return (dot > 0 ? file.name.slice(0, dot) : file.name).toLowerCase();
}

function isXml(file: File): boolean {
    return file.name.toLowerCase().endsWith('.xml');
}

export function groupIntoRequests(files: File[]): File[][] {
    const filesByBaseName = new Map<string, File[]>();
    files.forEach((file) => {
        const baseName = baseNameOf(file);
        filesByBaseName.set(baseName, [...(filesByBaseName.get(baseName) ?? []), file]);
    });
    return [...filesByBaseName.values()].flatMap((group) =>
        group.some(isXml) && group.some((file) => !isXml(file)) ? [group] : group.map((file) => [file]));
}

function isRetryableStatus(status: number): boolean {
    return status >= 500 || status === 408 || status === 429;
}

function retryAfterMsOf(response: Response): number | null {
    const seconds = Number(response.headers.get('Retry-After'));
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
}

const PENDING_STATUSES: ReadonlySet<UploadQueueStatus> = new Set(['queued', 'uploading']);
const MIN_SETTLED_FOR_ESTIMATE = 4;

export function useUploadQueue({ api, onAllSettled, retryBaseDelayMs = DEFAULT_RETRY_BASE_DELAY_MS }: UseUploadQueueOptions) {
    const [items, setItems] = useState<UploadQueueItem[]>([]);
    const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
    const [isStalled, setIsStalled] = useState(false);
    const isStalledRef = useRef(false);
    const runStartRef = useRef<{ at: number; settledCount: number } | null>(null);
    const itemsRef = useRef<Map<string, UploadQueueItem>>(new Map());
    const attemptsRef = useRef<Map<string, number>>(new Map());
    const activeCountRef = useRef(0);
    const hasUnreportedWorkRef = useRef(false);
    const consecutiveFailuresRef = useRef(0);
    const pausedUntilRef = useRef(0);
    const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const publishTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const abortControllerRef = useRef(new AbortController());
    const optionsRef = useRef({ api, onAllSettled, retryBaseDelayMs });
    const pumpRef = useRef<() => void>(() => undefined);

    useLayoutEffect(() => {
        optionsRef.current = { api, onAllSettled, retryBaseDelayMs };
    });

    const publishNow = useCallback(() => {
        if (publishTimerRef.current !== null) clearTimeout(publishTimerRef.current);
        publishTimerRef.current = null;
        const allItems = [...itemsRef.current.values()];
        const pendingCount = allItems.filter((item) => PENDING_STATUSES.has(item.status)).length;
        const settledCount = allItems.length - pendingCount;
        if (pendingCount === 0) {
            runStartRef.current = null;
        } else if (runStartRef.current === null) {
            runStartRef.current = { at: Date.now(), settledCount };
        }
        const settledThisRun = runStartRef.current ? settledCount - runStartRef.current.settledCount : 0;
        setSecondsLeft(runStartRef.current && settledThisRun >= MIN_SETTLED_FOR_ESTIMATE
            ? Math.round(((Date.now() - runStartRef.current.at) / 1000 / settledThisRun) * pendingCount)
            : null);
        setItems(allItems);
    }, []);

    const publish = useCallback(() => {
        if (publishTimerRef.current === null) publishTimerRef.current = setTimeout(publishNow, PUBLISH_INTERVAL_MS);
    }, [publishNow]);

    const update = useCallback((id: string, changes: Partial<UploadQueueItem>) => {
        const item = itemsRef.current.get(id);
        if (item) itemsRef.current.set(id, { ...item, ...changes });
    }, []);

    const send = useCallback(async (item: UploadQueueItem): Promise<UploadOutcome> => {
        const formData = new FormData();
        item.files.forEach((file) => formData.append('files', file, file.name));
        formData.append('document_category', item.direction);
        formData.append('shared_via', 'web');
        formData.append('upload_batch_id', item.uploadBatchId);
        const signal = abortControllerRef.current.signal;
        try {
            const response = await optionsRef.current.api.postFormData('document-collection/share-files', formData, signal, item.businessId);
            if (response.ok) return { kind: 'done' };
            if (!isRetryableStatus(response.status)) return { kind: 'rejected' };
            return { kind: 'retry', retryAfterMs: retryAfterMsOf(response) };
        } catch {
            return signal.aborted ? { kind: 'aborted' } : { kind: 'retry', retryAfterMs: null };
        }
    }, []);

    const pauseQueue = useCallback((retryAfterMs: number | null) => {
        consecutiveFailuresRef.current += 1;
        const backoffMs = optionsRef.current.retryBaseDelayMs * 2 ** (consecutiveFailuresRef.current - 1);
        const pauseMs = Math.min(Math.max(backoffMs, retryAfterMs ?? 0), MAX_PAUSE_MS);
        pausedUntilRef.current = Math.max(pausedUntilRef.current, Date.now() + pauseMs);
    }, []);

    const run = useCallback(async (id: string) => {
        const item = itemsRef.current.get(id);
        if (!item) return;
        activeCountRef.current += 1;
        update(id, { status: 'uploading', error: undefined });
        publish();

        const outcome = await send(item);
        activeCountRef.current -= 1;

        switch (outcome.kind) {
            case 'aborted':
                update(id, { status: 'queued' });
                return;
            case 'done':
                consecutiveFailuresRef.current = 0;
                if (isStalledRef.current) {
                    isStalledRef.current = false;
                    setIsStalled(false);
                }
                update(id, { status: 'done' });
                break;
            case 'rejected':
                update(id, { status: 'failed', error: 'rejected' });
                break;
            case 'retry': {
                const attempts = (attemptsRef.current.get(id) ?? 0) + 1;
                attemptsRef.current.set(id, attempts);
                update(id, attempts > MAX_RETRIES ? { status: 'failed', error: 'upload_failed' } : { status: 'queued' });
                pauseQueue(outcome.retryAfterMs);
                break;
            }
            default:
                throw new Error(`Unknown upload outcome ${JSON.stringify(outcome)}`);
        }
        publish();
        pumpRef.current();
    }, [pauseQueue, publish, send, update]);

    const pump = useCallback(() => {
        if (abortControllerRef.current.signal.aborted) return;
        const isSettled = ![...itemsRef.current.values()].some((item) => PENDING_STATUSES.has(item.status));
        if (isSettled && hasUnreportedWorkRef.current) {
            hasUnreportedWorkRef.current = false;
            publishNow();
            optionsRef.current.onAllSettled?.();
        }
        if (isSettled) {
            consecutiveFailuresRef.current = 0;
            pausedUntilRef.current = 0;
            if (isStalledRef.current) {
                isStalledRef.current = false;
                setIsStalled(false);
            }
        }
        if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES && !isSettled) {
            isStalledRef.current = true;
            setIsStalled(true);
        }
        if (isStalledRef.current) return;
        const pausedForMs = pausedUntilRef.current - Date.now();
        if (pausedForMs > 0) {
            if (resumeTimerRef.current === null) {
                resumeTimerRef.current = setTimeout(() => {
                    resumeTimerRef.current = null;
                    pumpRef.current();
                }, pausedForMs);
            }
            return;
        }
        for (const item of itemsRef.current.values()) {
            if (activeCountRef.current >= UPLOAD_CONCURRENCY) break;
            if (item.status === 'queued') void run(item.id);
        }
    }, [publishNow, run]);

    const resume = useCallback(() => {
        consecutiveFailuresRef.current = 0;
        pausedUntilRef.current = 0;
        if (resumeTimerRef.current !== null) clearTimeout(resumeTimerRef.current);
        resumeTimerRef.current = null;
        isStalledRef.current = false;
        setIsStalled(false);
        pump();
    }, [pump]);

    useLayoutEffect(() => {
        pumpRef.current = pump;
    }, [pump]);

    const enqueue = useCallback((files: File[], direction: DocumentDirection) => {
        const uniqueFiles = [...new Map(files.map((file) => [`${file.name}|${file.size}|${file.lastModified}`, file])).values()];
        if (uniqueFiles.length === 0) return;
        const isIdle = ![...itemsRef.current.values()].some((item) => PENDING_STATUSES.has(item.status));
        if (isIdle) {
            itemsRef.current.forEach((item, id) => {
                if (item.status === 'done') itemsRef.current.delete(id);
            });
        }
        const uploadBatchId = uuidv4();
        const businessId = getCurrentBusiness();
        groupIntoRequests(uniqueFiles).forEach((group) => {
            const id = uuidv4();
            itemsRef.current.set(id, { id, files: group, direction, uploadBatchId, businessId, status: 'queued' });
        });
        hasUnreportedWorkRef.current = true;
        publishNow();
        pump();
    }, [publishNow, pump]);

    const requeueFailed = useCallback((id: string) => {
        if (itemsRef.current.get(id)?.status !== 'failed') return;
        attemptsRef.current.delete(id);
        update(id, { status: 'queued', error: undefined });
        hasUnreportedWorkRef.current = true;
    }, [update]);

    const retry = useCallback((id: string) => {
        requeueFailed(id);
        publishNow();
        resume();
    }, [publishNow, requeueFailed, resume]);

    const retryAllFailed = useCallback(() => {
        [...itemsRef.current.keys()].forEach(requeueFailed);
        publishNow();
        resume();
    }, [publishNow, requeueFailed, resume]);

    const clearFinished = useCallback(() => {
        itemsRef.current.forEach((item, id) => {
            if (!PENDING_STATUSES.has(item.status)) itemsRef.current.delete(id);
        });
        publishNow();
    }, [publishNow]);

    const hasPendingUploads = items.some((item) => PENDING_STATUSES.has(item.status));

    useEffect(() => {
        setHasPendingUploads(hasPendingUploads);
        if (!hasPendingUploads) return;
        const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
            if (!hasConfirmedLeaving()) event.preventDefault();
        };
        window.addEventListener('beforeunload', warnBeforeLeaving);
        return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
    }, [hasPendingUploads]);

    useEffect(() => {
        abortControllerRef.current = new AbortController();
        pumpRef.current();
        return () => {
            abortControllerRef.current.abort();
            [resumeTimerRef, publishTimerRef].forEach((timerRef) => {
                if (timerRef.current !== null) clearTimeout(timerRef.current);
                timerRef.current = null;
            });
        };
    }, []);

    return { items, secondsLeft, isStalled, enqueue, retry, retryAllFailed, clearFinished, resume };
}
